import { useMemo, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Users as UsersIcon } from "lucide-react";
import { useStaff, useCreateStaff, useUpdateStaff, staffFullName, type StaffRole, type StaffRow } from "@/hooks/useStaff";

const ALL_ROLES: StaffRole[] = ["server", "driver"];

export default function StaffPage() {
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<StaffRow | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: staff, isLoading } = useStaff({ activeOnly: !showInactive });

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return staff ?? [];
    return (staff ?? []).filter((s) => {
      const hay = [s.first_name, s.last_name, s.email, s.phone].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(term);
    });
  }, [staff, search]);

  return (
    <DashboardLayout>
      <div className="space-y-6 p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-display font-semibold flex items-center gap-2">
              <UsersIcon className="h-6 w-6" /> Staff
            </h1>
            <p className="text-sm text-muted-foreground">
              Drivers and servers assignable to quotes. Soft-retire with the Active toggle; hard-deletes are blocked for anyone on an existing quote.
            </p>
          </div>
          <Button onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4 mr-1" /> Add Staff
          </Button>
        </div>

        <Card>
          <CardContent className="pt-5 space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
              <Input
                placeholder="Search name, email, phone…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="max-w-sm h-9"
              />
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                <Switch checked={showInactive} onCheckedChange={setShowInactive} /> Show inactive
              </label>
              <span className="text-xs text-muted-foreground ml-auto">
                {filtered.length} {filtered.length === 1 ? "person" : "people"}
              </span>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Roles</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && (
                    <TableRow><TableCell colSpan={5} className="text-sm text-muted-foreground">Loading…</TableCell></TableRow>
                  )}
                  {!isLoading && filtered.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-sm text-muted-foreground text-center py-8">No staff found.</TableCell></TableRow>
                  )}
                  {filtered.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="font-medium">{staffFullName(s) || "—"}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {s.roles.length === 0 && <span className="text-xs text-muted-foreground">—</span>}
                          {s.roles.map((r) => (
                            <Badge key={r} variant="outline" className="text-xs capitalize">{r}</Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        <div>{s.email ?? ""}</div>
                        <div>{s.phone ?? ""}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={s.is_active ? "default" : "secondary"} className="text-xs">
                          {s.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing(s)} aria-label={`Edit ${staffFullName(s)}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      <StaffFormDialog
        open={creating || !!editing}
        initial={editing}
        onClose={() => { setCreating(false); setEditing(null); }}
      />
    </DashboardLayout>
  );
}

interface FormState {
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  roles: StaffRole[];
  notes: string;
  is_active: boolean;
}

const EMPTY_FORM: FormState = {
  first_name: "",
  last_name: "",
  phone: "",
  email: "",
  roles: [],
  notes: "",
  is_active: true,
};

function StaffFormDialog({ open, initial, onClose }: { open: boolean; initial: StaffRow | null; onClose: () => void }) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const create = useCreateStaff();
  const update = useUpdateStaff();
  const saving = create.isPending || update.isPending;

  // Reset form whenever the dialog opens with different data
  useMemo(() => {
    if (!open) return;
    setForm(
      initial
        ? {
            first_name: initial.first_name,
            last_name: initial.last_name ?? "",
            phone: initial.phone ?? "",
            email: initial.email ?? "",
            roles: initial.roles,
            notes: initial.notes ?? "",
            is_active: initial.is_active,
          }
        : EMPTY_FORM
    );
  }, [open, initial]);

  const toggleRole = (r: StaffRole, on: boolean) => {
    setForm((f) => ({ ...f, roles: on ? Array.from(new Set([...f.roles, r])) : f.roles.filter((x) => x !== r) }));
  };

  const handleSave = async () => {
    if (!form.first_name.trim()) return;
    if (initial) {
      await update.mutateAsync({ id: initial.id, ...form });
    } else {
      await create.mutateAsync(form);
    }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit Staff" : "Add Staff"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">First name *</Label>
              <Input value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Last name</Label>
              <Input value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Roles</Label>
            <div className="flex items-center gap-4">
              {ALL_ROLES.map((r) => (
                <label key={r} className="flex items-center gap-2 text-sm capitalize cursor-pointer">
                  <Checkbox
                    checked={form.roles.includes(r)}
                    onCheckedChange={(v) => toggleRole(r, v === true)}
                  />
                  {r}
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Notes</Label>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Internal notes — not visible to customers"
            />
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-xs">Active</Label>
            <Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !form.first_name.trim()}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
